import { buildTrainingsDeskPath } from "@tennis-matches/core";
import { getUser } from "../auth/index.js";
import {
  confirmMobileSessionAttendance,
  getSessionAttendingPersonIds,
  getTrainingsDeskBacklogSessions,
  isSessionAttendanceConfirmDisabled,
  loadLocations,
  loadHubRelevantSessions,
  clearHubRelevantSessionsCache,
  loadTrainers,
  mergePeopleWithMissingIds,
  normalizeSessionPersonIdList,
  resolveLocationLabel,
  resolveSplitBaseForBilling,
  resolveViewerOwnTrainerIds,
} from "../trainerstunden/index.js";
import {
  isHubReadScopeCurrent,
  registerHubCacheClear,
  resolveHubReadScope,
} from "./hub-scoped-cache.js";

function asStr(v) {
  return String(v ?? "").trim();
}

function sessionTitle(session) {
  return (
    asStr(session?.title) ||
    asStr(session?.group_name) ||
    asStr(session?.label) ||
    ""
  );
}

function sessionTimeRange(session) {
  const start = asStr(session?.start_time).slice(0, 5);
  const end = asStr(session?.end_time).slice(0, 5);
  if (start && end) return `${start}–${end}`;
  return start || end || "";
}

function compareSessionsChronoAsc(a, b) {
  const dc = asStr(a?.date).localeCompare(asStr(b?.date));
  if (dc !== 0) return dc;
  return asStr(a?.start_time).localeCompare(asStr(b?.start_time));
}

function personDisplayName(person, fallbackId = "") {
  const full =
    `${asStr(person?.first_name)} ${asStr(person?.last_name)}`.trim();
  if (full) return full;
  const email = asStr(person?.email);
  if (email) return email;
  // Roh-IDs / Codes nie als „Name“ anzeigen
  const fallback = asStr(fallbackId);
  if (
    !fallback ||
    /^[0-9a-f-]{36}$/i.test(fallback) ||
    /^[A-Z0-9_-]{6,}$/.test(fallback)
  ) {
    return "";
  }
  return fallback;
}

function resolveConfirmPeopleNames(session, peopleById) {
  let ids = getSessionAttendingPersonIds(session);
  if (!ids.length) ids = normalizeSessionPersonIdList(session?.planned_ids);
  return ids
    .map((id) => personDisplayName(peopleById.get(id), id))
    .filter(Boolean);
}

function formatPeopleLabel(names, maxVisible = 3) {
  const list = (Array.isArray(names) ? names : [])
    .map((n) => asStr(n))
    .filter(Boolean);
  if (!list.length) return "";
  if (list.length <= maxVisible) return list.join(", ");
  return `${list.slice(0, maxVisible).join(", ")} +${list.length - maxVisible}`;
}

function collectConfirmPeopleIds(sessions) {
  const out = [];
  const seen = new Set();
  for (const session of sessions || []) {
    for (const id of [
      ...getSessionAttendingPersonIds(session),
      ...normalizeSessionPersonIdList(session?.planned_ids),
    ]) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

/**
 * True wenn Abrechnung Show/No-Show pro Person braucht (kein Hub-Schnellconfirm).
 * @param {object} session
 */
export function isHubTrainerConfirmNeedsAttendanceUi(session) {
  return resolveSplitBaseForBilling(session) === "present";
}

/**
 * Hub-Schnellconfirm (Ja/Nein) nur ohne Show/No-Show-UI und wenn Confirm erlaubt.
 * @param {object} session
 * @param {object[]} [trainers]
 */
export function canHubTrainerQuickConfirmSession(session, trainers = []) {
  if (isSessionAttendanceConfirmDisabled(session, trainers)) return false;
  if (isHubTrainerConfirmNeedsAttendanceUi(session)) return false;
  return true;
}

/**
 * Vergangene unbestätigte Sessions des Viewers als Trainer (gesamter Backlog).
 *
 * @param {object} opts
 * @param {object[]} [opts.sessions]
 * @param {string[]} [opts.myTrainerIds]
 * @param {Date} [opts.now]
 * @returns {object[]}
 */
export function listHubTrainerConfirmBacklogSessions({
  sessions = [],
  myTrainerIds = [],
  now = new Date(),
} = {}) {
  const mine = new Set(
    (Array.isArray(myTrainerIds) ? myTrainerIds : [])
      .map((id) => asStr(id))
      .filter(Boolean),
  );
  if (!mine.size) return [];

  return getTrainingsDeskBacklogSessions(sessions, now)
    .filter((session) => {
      const tid = asStr(session?.trainer_id);
      return Boolean(tid && mine.has(tid));
    })
    .sort(compareSessionsChronoAsc);
}

/**
 * Vergangene unbestätigte Sessions mit Hub-Schnellconfirm (ohne present-Split).
 *
 * @param {object} opts
 * @param {object[]} [opts.sessions]
 * @param {object[]} [opts.trainers]
 * @param {string[]} [opts.myTrainerIds]
 * @param {Date} [opts.now]
 * @returns {object[]}
 */
export function listHubTrainerSimpleConfirmSessions({
  sessions = [],
  trainers = [],
  myTrainerIds = [],
  now = new Date(),
} = {}) {
  return listHubTrainerConfirmBacklogSessions({
    sessions,
    myTrainerIds,
    now,
  }).filter((session) => canHubTrainerQuickConfirmSession(session, trainers));
}

export function buildHubTrainerConfirmSessionHref(sessionId) {
  const id = asStr(sessionId);
  const base = buildTrainingsDeskPath("planung");
  if (!id) return base;
  const sp = new URLSearchParams();
  sp.set("session", id);
  sp.set("returnTo", "/hub");
  return `${base}?${sp.toString()}`;
}

export function buildHubTrainerConfirmEditHref(sessionId) {
  const id = asStr(sessionId);
  const base = buildTrainingsDeskPath("planung");
  if (!id) return base;
  return `${base}?session=${encodeURIComponent(id)}&mode=edit`;
}

export function buildHubTrainerConfirmNewSessionHref() {
  return `${buildTrainingsDeskPath("planung")}?newSession=1`;
}

export function buildHubTrainerConfirmDeskHref(sessionId) {
  const id = asStr(sessionId);
  const base = `${buildTrainingsDeskPath("planung")}?mode=confirm`;
  return id ? `${base}&session=${encodeURIComponent(id)}` : base;
}

/**
 * @param {object} session
 * @param {{ people?: object[], locations?: object[], trainers?: object[] }} [ctx]
 * @returns {{
 *   id: string,
 *   title: string,
 *   peopleNames: string[],
 *   peopleLabel: string,
 *   locationLabel: string,
 *   date: string,
 *   timeRange: string,
 *   canQuickConfirm: boolean,
 *   editHref: string,
 *   detailHref: string,
 *   deskConfirmHref: string,
 *   newSessionHref: string,
 *   raw: object,
 * }}
 */
export function mapHubTrainerConfirmSessionView(session, ctx = {}) {
  const id = asStr(session?.id);
  const people = Array.isArray(ctx.people) ? ctx.people : [];
  const locations = Array.isArray(ctx.locations) ? ctx.locations : [];
  const trainers = Array.isArray(ctx.trainers) ? ctx.trainers : [];
  const peopleById = new Map(
    people.map((p) => [asStr(p?.id), p]).filter(([pid]) => pid),
  );
  const peopleNames = resolveConfirmPeopleNames(session, peopleById);
  return {
    id,
    title: sessionTitle(session),
    peopleNames,
    peopleLabel: formatPeopleLabel(peopleNames),
    locationLabel: resolveLocationLabel(session, locations),
    date: asStr(session?.date).slice(0, 10),
    timeRange: sessionTimeRange(session),
    canQuickConfirm: canHubTrainerQuickConfirmSession(session, trainers),
    editHref: buildHubTrainerConfirmEditHref(id),
    detailHref: buildHubTrainerConfirmSessionHref(id),
    deskConfirmHref: buildHubTrainerConfirmDeskHref(id),
    newSessionHref: buildHubTrainerConfirmNewSessionHref(),
    raw: session,
  };
}

const HUB_TRAINER_CONFIRM_CACHE_MS = 15000;
/** @type {Map<string, Promise<object[]>>} */
const __confirmInflight = new Map();
/** @type {Map<string, { ts: number, value: object[] }>} */
const __confirmCache = new Map();

export function clearHubTrainerConfirmSessionsCache() {
  __confirmCache.clear();
  __confirmInflight.clear();
}

registerHubCacheClear(clearHubTrainerConfirmSessionsCache);

/**
 * @param {{ force?: boolean }} [opts]
 * @returns {Promise<object[]>} view models
 */
export async function loadHubTrainerConfirmSessions({ force = false } = {}) {
  const scope = await resolveHubReadScope();
  const cacheKey = scope.key;
  const now = Date.now();
  if (!force) {
    const cached = __confirmCache.get(cacheKey);
    if (cached && now - cached.ts < HUB_TRAINER_CONFIRM_CACHE_MS) {
      return cached.value;
    }
    const inflight = __confirmInflight.get(cacheKey);
    if (inflight) return inflight;
  }

  const started = { ...scope };
  const promise = __loadHubTrainerConfirmSessionsImpl()
    .then((value) => {
      if (isHubReadScopeCurrent(started)) {
        __confirmCache.set(cacheKey, { ts: Date.now(), value });
      }
      return value;
    })
    .finally(() => {
      if (__confirmInflight.get(cacheKey) === promise) {
        __confirmInflight.delete(cacheKey);
      }
    });

  __confirmInflight.set(cacheKey, promise);
  return promise;
}

async function __loadHubTrainerConfirmSessionsImpl() {
  const user = await getUser().catch(() => null);
  const email = asStr(user?.email);
  if (!email && !asStr(user?.id)) return [];

  const [sessionsRes, trainersRes, locationsRes] = await Promise.all([
    loadHubRelevantSessions().catch(() => ({ ok: false, data: [] })),
    loadTrainers().catch(() => ({ ok: false, data: [] })),
    loadLocations().catch(() => ({ ok: false, data: [] })),
  ]);
  const sessions = sessionsRes?.ok ? sessionsRes.data || [] : [];
  const trainers = trainersRes?.ok ? trainersRes.data || [] : [];
  const myTrainerIds = resolveViewerOwnTrainerIds(trainers, {
    authEmail: email,
  });
  if (!myTrainerIds.length) return [];

  const confirmSessions = listHubTrainerConfirmBacklogSessions({
    sessions,
    myTrainerIds,
  });
  const neededPeopleIds = collectConfirmPeopleIds(confirmSessions);
  const mergedPeople = await mergePeopleWithMissingIds(
    [],
    neededPeopleIds,
  ).catch(() => null);
  const people =
    mergedPeople?.ok && Array.isArray(mergedPeople.data)
      ? mergedPeople.data
      : [];
  const locations = locationsRes?.ok ? locationsRes.data || [] : [];

  return confirmSessions.map((session) =>
    mapHubTrainerConfirmSessionView(session, { people, locations, trainers }),
  );
}

/**
 * @param {object} sessionOrView
 * @returns {Promise<{ ok: boolean, errorKey?: string }>}
 */
export async function confirmHubTrainerSessionAttendance(sessionOrView) {
  const session = sessionOrView?.raw || sessionOrView;
  const result = await confirmMobileSessionAttendance(session);
  if (result?.ok) {
    clearHubTrainerConfirmSessionsCache();
    clearHubRelevantSessionsCache();
  }
  return result;
}
